/**
 * §13.1.11 Resume Prompt. Shown from the title when a mid-run snapshot exists.
 * Abandon clears the snapshot behind an inline hold-confirm. A snapshot whose
 * content hash no longer matches this build reports the mismatch and offers only
 * Abandon — it never crashes (A.6 migration rules).
 */

import { button, div, holdConfirm, panel, panelBody, setHidden, setText } from '../dom';
import { formatInt, formatTime, humanizeId } from '../format';
import type { ResumeInfo } from '../types';
import { Screen, type ActionSink } from './base';

export class ResumePromptScreen extends Screen {
  private line: HTMLElement;
  private notice: HTMLElement;
  private resumeBtn: HTMLButtonElement;

  constructor(emit: ActionSink) {
    super('resume-prompt', emit, 'is-modal');

    const p = panel('resume-panel');
    const body = panelBody(p);
    body.appendChild(div('screen-title', 'RESUME RUN'));
    this.line = div('resume-line', '');
    body.appendChild(this.line);
    this.notice = div('resume-notice', 'This save was made on an older build. Balance has changed, so it cannot be resumed.');
    this.notice.hidden = true;
    body.appendChild(this.notice);

    const menu = div('resume-menu');
    this.resumeBtn = button('Resume', 'primary', 'play');
    this.resumeBtn.addEventListener('click', () => this.emit({ type: 'resume_run' }));
    const abandon = button('Abandon', 'danger', 'trash');
    holdConfirm(abandon, 'Abandon', 'Hold to confirm', () => this.emit({ type: 'abandon_save' }));
    const back = button('Back', 'ghost', 'angle-left');
    back.addEventListener('click', () => this.emit({ type: 'goto', screen: 'title' }));
    menu.appendChild(this.resumeBtn);
    menu.appendChild(abandon);
    menu.appendChild(back);
    body.appendChild(menu);

    this.root.appendChild(p);
    this.stops = [this.resumeBtn, abandon, back];
  }

  present(info: ResumeInfo): void {
    setText(
      this.line,
      formatTime(info.time_s) + ' · Lv ' + formatInt(info.level) + ' · ' + humanizeId(info.difficulty_id),
    );
    setHidden(this.notice, info.compatible);
    this.resumeBtn.disabled = !info.compatible;
    this.resumeBtn.classList.toggle('is-disabled', !info.compatible);
  }

  override onBack(): boolean {
    this.emit({ type: 'goto', screen: 'title' });
    return true;
  }
}
